import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Checkbox, Flex, Form, Input, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { AgentCommonFilterFields } from "../../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import ComponentWithPermission from "../../../../auth/components/ComponentWithPermission";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionsCategory } from "../../../enums";
import { DEFAULT_FILTER_COMMISSIONS_OUTPUTS_QUERY_PARAMS } from "../../api";
import { CommissionsOutputReport } from "../../enums";
import { CommissionsOutputFilterPageRequest } from "../../types";
import { CommissionsOutputModalForm } from "./CommissionsOutputModalForm";

type ExtendedFilter = { completedEducationDateRange?: string[]; completedAcademyDateRange?: string[] };

type Props = {
  searchParams: CommissionsOutputFilterPageRequest;
};

export const CommissionsOutputsFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [isOpenCreateUpdateModal, setIsOpenCreateUpdateModal] = useState(false);

  const [form] = Form.useForm<CommissionsOutputFilterPageRequest & ExtendedFilter>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      completedEducationDateRange:
        searchParams.completedEducationDateMin && searchParams.completedEducationDateMax
          ? [searchParams.completedEducationDateMin, searchParams.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        searchParams.completedAcademyDateMin && searchParams.completedAcademyDateMax
          ? [searchParams.completedAcademyDateMin, searchParams.completedAcademyDateMax]
          : undefined,
    });
  }, [searchParams]);

  const handleFormSubmit = ({
    completedEducationDateRange,
    completedAcademyDateRange,
    ...filter
  }: CommissionsOutputFilterPageRequest & ExtendedFilter) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_FILTER_COMMISSIONS_OUTPUTS_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("commissions.outputs.titles.page")}</h2>}
      extra={
        <Space>
          <ComponentWithPermission permissions={[Permission.COMMISSIONS_MANAGE]}>
            <Button type="primary" icon={<AntIcon type="plus" />} onClick={() => setIsOpenCreateUpdateModal(true)}>
              {t("commissions.outputs.actions.add")}
            </Button>
          </ComponentWithPermission>

          <Button
            color="danger"
            variant="outlined"
            icon={<AntIcon type="reload" />}
            onClick={() => {
              form.resetFields();
              handleFormSubmit(form.getFieldsValue());
            }}
          >
            {t("common.resetFilter")}
          </Button>

          <CommissionsOutputModalForm
            isOpen={isOpenCreateUpdateModal}
            onCancel={() => setIsOpenCreateUpdateModal(false)}
          />
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        name="commissionsOutputsFilterForm"
        onFinish={handleFormSubmit}
        initialValues={{
          onlyDirectSubordinates: false,
          includeDeactivated: true,
          includeRepresentatives: false,
          includeNonGainers: true,
          includeUnprocessed: false,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields>
            <Form.Item
              name="report"
              label={t("commissions.enums.commissionsOutputReport._label")}
              rules={[validations.none]}
              initialValue={CommissionsOutputReport.ALL}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.values(CommissionsOutputReport).map((item) => ({
                  value: item,
                  label: t(`commissions.enums.commissionsOutputReport.${item}`),
                }))}
              />
            </Form.Item>
          </AgentCommonFilterFields>

          {isTopAgentPlus ? (
            <Form.Item
              name="categories"
              label={t("commissions.enums.commissionsCategory_PLUS._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                mode="multiple"
                allowClear
                options={Object.values(CommissionsCategory)
                  .filter((category) => category !== CommissionsCategory.PRIMARY)
                  .map((category) => ({
                    value: category,
                    label: t(`commissions.enums.commissionsCategory_PLUS.${category}`),
                  }))}
              />
            </Form.Item>
          ) : undefined}

          <Form.Item
            name="batchKeyword"
            label={t("commissions.outputs.filter.batchKeyword")}
            rules={[validations.none]}
          >
            <Input placeholder={t("commissions.outputs.helpers.searchHint")} />
          </Form.Item>

          <Form.Item name="includeUnprocessed" valuePropName="checked" rules={[validations.none]}>
            <Checkbox>{t("commissions.outputs.filter.includeUnprocessed")}</Checkbox>
          </Form.Item>
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              handleFormSubmit(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
